import throttle from 'lodash/throttle'
import isEqual from 'lodash/isEqual'
import type { IPoint } from '@/modules/Editor/point'
import type { View } from '@/modules/Editor/views/view'
import { Rectangle } from '@/modules/Editor/rectangle'
import { drawDashedBox } from '@/modules/Editor/graphicsV2/drawDashedBox'
import { EditorCursor, selectCursor } from '@/modules/Editor/editorCursor'
import { findAnnotationsIntersectingMultiselectRectangle } from './utils'

/**
 * Manages CMD+drag multi-selection rectangle functionality for the EditTool
 *
 * Handles the visual rectangle drawing and selection logic when users hold CMD
 * and drag to select multiple annotations within a rectangular area.
 *
 * Responsibilities:
 * - Tracking rectangle selection state (start, current, intersections)
 * - Drawing the selection rectangle on canvas with dashed border
 * - Calculating annotation intersections with throttling for performance
 * - Managing cumulative selection (original + intersecting annotations)
 */
export class MultiSelectionRectangle {
  public isDrawing = false
  private startPoint?: IPoint
  private currentPoint?: IPoint
  private originalSelection: string[] = []
  private currentIntersections: Set<string> = new Set()

  /**
   * Throttled intersection calculation for performance during fast mouse movements.
   * Updates selection only after the throttle delay, while visual drawing remains immediate.
   */
  private updateSelection = throttle((view: View, start: IPoint, current: IPoint): void => {
    // Calculate live selection intersections
    const newIntersections = findAnnotationsIntersectingMultiselectRectangle(view, start, current)

    // Skip updating selection if intersections is un-changed
    if (isEqual(Array.from(this.currentIntersections), newIntersections)) {
      return
    }

    this.currentIntersections = new Set(newIntersections)

    // Final selection = original selection (unchanged) + current intersections
    const finalSelection = [...this.originalSelection, ...newIntersections]
    const uniqueSelection = Array.from(new Set(finalSelection))

    view.annotationManager.selectAnnotations(uniqueSelection)
  }, 100)

  /**
   * Starts rectangle selection at the given point
   */
  startSelection(point: IPoint, currentSelection: string[]): void {
    this.isDrawing = true
    this.startPoint = point
    this.currentPoint = undefined
    this.originalSelection = [...currentSelection]
    this.currentIntersections.clear()
    selectCursor(EditorCursor.BBox)
  }

  /**
   * Updates the current end point of the rectangle and triggers drawing/selection
   */
  update(point: IPoint, view: View): void {
    if (!this.isDrawing || !this.startPoint) return

    this.currentPoint = point

    // Draw multi-select rectangle using the same style as auto-annotate rectangle
    const rectangle = new Rectangle(this.startPoint, this.currentPoint)
    view.annotationsLayer.draw((ctx) => drawDashedBox(ctx, rectangle))

    // Calculate live selection intersections (throttled)
    this.updateSelection(view, this.startPoint, this.currentPoint)
  }

  /**
   * Completes the rectangle selection
   */
  complete(): void {
    if (!this.isDrawing) return

    // Future: Add any completion logic here
    this.reset()
  }

  /**
   * Resets all rectangle selection state
   */
  reset(): void {
    const wasDrawing = this.isDrawing

    this.isDrawing = false
    this.startPoint = undefined
    this.currentPoint = undefined
    this.originalSelection = []
    this.currentIntersections.clear()

    // Cancel any pending throttled operations
    this.updateSelection.cancel()

    // Reset cursor if we were drawing a rectangle
    if (wasDrawing) {
      selectCursor(EditorCursor.Move)
    }
  }

  /**
   * Clears the visual rectangle from the canvas
   */
  clearDrawing(view: View): void {
    view.annotationsLayer.draw() // Clear rectangle drawing
  }
}
